import React from 'react';
import {Box, styled} from '@mui/material';
import {CollaborationGigBase} from '@/types/gigs';
import {
  StyledCardFrame,
  StyledCardImage,
  StyledCardContent,
  StyledCardHeader,
  StyledHeaderTop,
  StyledBrand,
  StyledTitle,
} from './common';
import CollaborationGigMetadata from './CollaborationGigMetadata';
import CollaborationGigFooter from './CollaborationGigFooter';
import {TagBanner, TagBannerProps} from './TagBanner';
import translate from '@/utils/translate';
import FlameIcon from '@/components/icons/Flame';
import Conditional from '@/components/conditional';

const OBJECTIVE_PERCENT_RANGE = {
  MIN: 80,
  MAX: 100,
};

const METADATA_HEIGHT_REGULAR = '88px';
const METADATA_HEIGHT_WITH_OBJ_PERCENT = '48px';

interface CollaborationGigCardProps {
  gigData: CollaborationGigBase;
  className?: string;
  tag?: TagBannerProps;
}

const StyledObjectivePercent = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '18px',
  marginInline: '16px',
  marginTop: 'auto',
  justifyContent: 'flex-start',
}));

const StyledObjectivePercentText = styled('span')(() => ({
  fontSize: '13px',
  lineHeight: '24px',
  marginLeft: '5px',
  marginBottom: '-4px',
}));

/**
 * CollaborationGigCard component for displaying collaboration opportunities.
 * Used for "Suggested Gigs" and "Invites" sections.
 */
function CollaborationGigCard({
  gigData,
  className,
  tag,
}: CollaborationGigCardProps) {
  const {
    brand,
    campaignName,
    campaignImage,
    gender,
    location,
    ageRange,
    objectivePercent,
  } = gigData;

  const campaignAlmostFull =
    objectivePercent > OBJECTIVE_PERCENT_RANGE.MIN &&
    objectivePercent < OBJECTIVE_PERCENT_RANGE.MAX;

  return (
    <StyledCardFrame className={className}>
      <StyledCardImage imageUrl={campaignImage} />

      {tag && <TagBanner text={tag.text} icon={tag.icon} color={tag.color} />}

      <StyledCardContent>
        <StyledCardHeader>
          <StyledHeaderTop>
            <StyledBrand size="md">{brand}</StyledBrand>
          </StyledHeaderTop>
          <StyledTitle size="lg" className="card-title">
            {campaignName}
          </StyledTitle>
        </StyledCardHeader>

        {/* Creator metadata section */}
        <CollaborationGigMetadata
          gender={gender}
          ageRange={ageRange}
          location={location}
          totalHeight={
            campaignAlmostFull
              ? METADATA_HEIGHT_WITH_OBJ_PERCENT
              : METADATA_HEIGHT_REGULAR
          }
        />

        {/* Objective percent section */}
        <Conditional condition={campaignAlmostFull}>
          <StyledObjectivePercent>
            <FlameIcon height={22} />
            <StyledObjectivePercentText>
              {translate('Hurry! Campaign is {percent}% full', {
                percent: objectivePercent,
              })}
            </StyledObjectivePercentText>
          </StyledObjectivePercent>
        </Conditional>
      </StyledCardContent>

      {/* Footer with key stats */}
      <CollaborationGigFooter gigData={gigData} />
    </StyledCardFrame>
  );
}

export default CollaborationGigCard;
